/**
 * Option ECharts du radar de matching d'un entretien structuré — module PUR.
 *
 * Extrait de `EntStructMatchingChart.tsx`, qui n'en est plus que l'enveloppe
 * React. La raison : le rapport PDF rend le MÊME graphe côté serveur, via
 * `generate-echarts-image`, et un graphe redessiné à la main pour le document
 * finirait par diverger de l'écran. Un seul constructeur, deux rendus — comme
 * `@repo/pami` et `@repo/fit-culturel`.
 */

import type { ScoresCalcules } from "./scores";

/**
 * Une compétence de l'entretien confrontée à ce que le candidat a couvert.
 *
 * Forme volontairement MINIMALE : `lignesMatchingEntStruct` la produit depuis
 * `calculerScores`.
 */
export type LigneMatchingEntStruct = {
  competence: string;
  /** Poids de la compétence dans le barème du template (`competence_weight`). */
  poids: number;
  /** Taux de couverture 0-100 : `scoreObtenu / scoreMax`. */
  couverture: number;
  scoreObtenu: number;
  scoreMax: number;
  /** Détail hiérarchique, si le template en porte. Infobulle uniquement. */
  sousCompetences?: { nom: string; scoreObtenu: number; scoreMax: number }[];
};

/** Compétence de mise en route, hors barème : ni scorée, ni représentée. */
export const COMPETENCE_NEUTRE = "Question neutre";

/** Les lignes du radar, depuis les scores d'un entretien. */
export function lignesMatchingEntStruct(
  scores: ScoresCalcules,
): LigneMatchingEntStruct[] {
  return scores.competences
    .filter((c) => c.nom !== COMPETENCE_NEUTRE)
    .map((c) => ({
      competence: c.nom,
      poids: c.poids,
      couverture: c.scoreMax > 0 ? (c.scoreObtenu / c.scoreMax) * 100 : 0,
      scoreObtenu: c.scoreObtenu,
      scoreMax: c.scoreMax,
      sousCompetences: c.sousCompetences?.map((sc) => ({
        nom: sc.nom,
        scoreObtenu: sc.scoreObtenu,
        scoreMax: sc.scoreMax,
      })),
    }));
}

/**
 * Repli de la couleur `primary` du design system, en hexadécimal : l'équivalent
 * exact de `oklch(0.5338 0.2503 301.37)` (`packages/tailwind-config`). Il ne
 * sert qu'au rendu serveur et aux contextes sans CSS chargé — en navigateur,
 * c'est le token qui gagne, thème sombre compris.
 */
export const PRIMARY_FALLBACK = "#8a2be2";

/**
 * Lit un token du design system. ECharts veut des chaînes de couleur, pas des
 * classes : sans ce détour, le rebrand devrait repasser dans les graphes.
 * `repli` ne sert qu'au rendu serveur et aux contextes sans CSS chargé.
 */
function jeton(nom: string, repli: string): string {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return repli;
  }
  const valeur = getComputedStyle(document.documentElement)
    .getPropertyValue(nom)
    .trim();
  return valeur || repli;
}

/** Trait de séparation entre deux compétences : pointillé léger et UNIFORME.
 *  La grille sépare, elle ne hiérarchise pas — c'est au fond des secteurs de
 *  porter le poids. */
const TRAIT_SECTEUR = "rgba(0,0,0,0.12)";

/** Opacité plancher : une compétence de poids négligeable doit s'effacer, jamais
 *  disparaître — un secteur invisible se lirait « compétence absente ». */
const OPACITE_MIN = 0.08;
const OPACITE_MAX = 0.55;

/**
 * Intensité du fond d'un secteur : le poids RELATIF au poids le plus lourd du
 * barème, et non sa part de la somme. Sur huit compétences quasi équipondérées,
 * une normalisation par la somme les rendrait toutes également pâles et le canal
 * ne dirait plus rien ; rapportée au maximum, la plus lourde sature et les
 * autres se situent par rapport à elle.
 *
 * Plafonnée à 0.55 : au-delà, le fond concurrence le polygone du candidat, qui
 * est la donnée, alors que le poids n'est que le contexte de lecture.
 */
function opacitePoids(poids: number, poidsMax: number): number {
  if (poidsMax <= 0) return OPACITE_MAX;
  const part = Math.max(0, Math.min(1, poids / poidsMax));
  return OPACITE_MIN + part * (OPACITE_MAX - OPACITE_MIN);
}

/**
 * Coupe un nom trop long aux espaces, en AUTANT de lignes que nécessaire.
 *
 * Deux lignes ne suffisent pas aux intitulés de référentiel métier
 * (« Développer l'efficacité commerciale d'une unité marchande dans un
 * environnement omnicanal ») : la seconde, de cinquante signes, sortait du
 * canevas. Un mot plus long que la limite reste sur sa ligne — mieux vaut
 * dépasser un peu que couper un mot en deux. Même règle que `couperNom` de
 * `@repo/fit-culturel`.
 */
function couperNom(nom: string, longueurMax: number): string {
  if (nom.length <= longueurMax) return nom;
  const lignes: string[] = [];
  let courante = "";
  for (const mot of nom.split(" ")) {
    const suivant = (courante + " " + mot).trim();
    if (courante && suivant.length > longueurMax) {
      lignes.push(courante);
      courante = mot;
    } else {
      courante = suivant;
    }
  }
  if (courante) lignes.push(courante);
  return lignes.join("\n");
}

const arrondi1 = (n: number) => Math.round(n * 10) / 10;

export type OptionMatchingEntStructParams = {
  titre?: string;
  /**
   * Rendu destiné à une image fixe : ni animation, ni boîte à outils, ni
   * infobulle — rien de tout cela ne survit à un PNG, et l'animation fait
   * capturer le graphe à mi-course. Les textes grossissent dans la proportion
   * où l'image sera réduite dans le document.
   */
  printMode?: boolean;
  /**
   * L'option part en JSON vers `generate-echarts-image`, et `JSON.stringify`
   * supprime les fonctions SANS RIEN SIGNALER. Ce mode les retire en amont.
   */
  serializable?: boolean;
};

/**
 * Radar de matching d'un entretien structuré : couverture par compétence, fond
 * de secteur pondéré.
 *
 * DEUX COUCHES SUPERPOSÉES, sur des géométries alignées — même grammaire que
 * `PamiMatchingChart`, pour que les deux figures se lisent pareil :
 *
 * 1. Les **secteurs du barème**, en barres polaires allant du centre au bord.
 *    Leur étendue radiale est constante : le secteur n'est pas une donnée, c'est
 *    le contexte. Seule son OPACITÉ varie, et elle code le poids de la
 *    compétence dans le barème du template.
 * 2. La **couverture du candidat**, en radar : polygone fermé sans remplissage
 *    (il masquerait les secteurs) plus un point par compétence.
 *
 * Le poids n'entre PAS dans le rayon. Le rayon dit une seule chose — la part des
 * critères couverts — et la pondération est lue sur un canal séparé, sinon le
 * même fait serait codé deux fois et une compétence lourde mal couverte
 * ressemblerait à une compétence légère bien couverte.
 *
 * L'alignement des deux systèmes de coordonnées vient du triplet
 * `boundaryGap: false` + `clockwise: false` + `startAngle: 90` : sans lui, chaque
 * point serait décalé d'un demi-secteur par rapport à son propre fond.
 */
export function buildEntStructMatchingOption(
  lignes: LigneMatchingEntStruct[],
  {
    titre = "Couverture par compétence",
    printMode = false,
    serializable = false,
  }: OptionMatchingEntStructParams = {},
): Record<string, unknown> {
  const poidsMax = lignes.reduce((max, l) => Math.max(max, l.poids), 0);
  const categories = lignes.map((l) => l.competence);
  /** Demi-largeur d'un secteur, en degrés : le décalage exact qui place un
   *  séparateur à mi-chemin entre deux compétences voisines. */
  const demiSecteur = categories.length > 0 ? 180 / categories.length : 0;
  // Résolue au calcul de l'option, donc relue à chaque re-rendu : un
  // changement de thème repeint les secteurs sans code supplémentaire.
  const couleur = jeton("--primary", PRIMARY_FALLBACK);
  const encre = jeton("--encre", "#111827");
  const surface = jeton("--surface", "#ffffff");

  // L'image du document est rendue sur 1200 px puis réduite à ~150 mm : tout
  // ce qui est écrit grossit dans la même proportion que pour le PAMI et le fit
  // culturel. Les intitulés de compétence sont longs — jusqu'à six lignes en
  // haut du cercle — d'où un rayon qui leur laisse la place sous le titre.
  const rayon = printMode ? "58%" : "62%";
  const centre = printMode ? ["50%", "54%"] : ["50%", "54%"];
  // 21 px sur 1200, une fois l'image réduite à 140 mm : ≈ 7 pt, le plancher
  // de lisibilité d'une légende imprimée. À 17, les libellés tombaient à 6 pt.
  const policeNom = printMode ? 21 : 11;
  const interligneNom = printMode ? 26 : 13;
  const policeTitre = printMode ? 22 : 15;
  const taillePoint = printMode ? 14 : 9;
  const epaisseurProfil = printMode ? 3 : 2;
  // Signes par ligne d'étiquette : à police égale, c'est la largeur du bloc
  // qui décide s'il tient entre le cercle et le bord du canevas.
  const largeurNom = printMode ? 18 : 16;

  const infobulle =
    printMode || serializable
      ? { show: false }
      : {
          trigger: "item",
          confine: true,
          // Décalée du curseur et non-survolable : posée SOUS le pointeur, la
          // boîte volerait le survol au secteur, qui la ferait disparaître —
          // d'où un clignotement en boucle.
          enterable: false,
          position: (point: [number, number]) => [point[0] + 16, point[1] + 16],
          transitionDuration: 0,
          // Une seule infobulle, portée par les secteurs : elle dit TOUT ce qui
          // concerne la compétence survolée — poids, couverture, détail.
          formatter: (params: { dataIndex: number }) => {
            const ligne = lignes[params.dataIndex];
            if (!ligne) return "";
            const detail = (ligne.sousCompetences ?? []).map(
              (sc) =>
                `&nbsp;&nbsp;· ${sc.nom} : ${arrondi1(sc.scoreObtenu)}/${arrondi1(
                  sc.scoreMax,
                )}`,
            );
            return [
              `<b>${ligne.competence}</b>`,
              `Poids dans le barème : ${arrondi1(ligne.poids)}`,
              `Couverture : ${Math.round(ligne.couverture)} % (${arrondi1(
                ligne.scoreObtenu,
              )}/${arrondi1(ligne.scoreMax)} pts)`,
              ...detail,
            ].join("<br/>");
          },
        };

  return {
    animation: !printMode,
    title: {
      text: titre,
      left: "center",
      top: 8,
      textStyle: {
        fontSize: policeTitre,
        fontFamily: "sans-serif",
        fontWeight: 600,
      },
    },
    tooltip: infobulle,
    // Pas de légende : tous les secteurs partagent la même teinte, il n'y a
    // aucune couleur à décoder. L'échelle des poids est expliquée par la
    // légende du composant parent (ou le texte du document), et chaque poids
    // est écrit dans l'infobulle — l'opacité n'est jamais le seul porteur de
    // l'information.
    legend: { show: false },
    toolbox: {
      show: !printMode,
      feature: { saveAsImage: { show: true } },
    },

    // ── Couche 1 : secteurs du barème ──────────────────────────────────
    polar: [
      { radius: ["0%", rayon], center: centre },
      // Polaire jumeau, dédié aux SÉPARATEURS. Il ne porte aucune donnée :
      // son seul rôle est de fournir des `splitLine` décalées d'un demi-secteur,
      // donc tracées ENTRE deux compétences et non au travers de l'une d'elles.
      { radius: ["0%", rayon], center: centre },
    ],
    angleAxis: [
      {
        type: "category",
        data: categories,
        // Collé à l'axe + sens trigonométrique + départ à 90° : c'est CE
        // triplet qui aligne les secteurs sur les sommets du radar.
        boundaryGap: false,
        clockwise: false,
        startAngle: 90,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
      },
      {
        polarIndex: 1,
        // Axe NUMÉRIQUE 0–360 et non catégoriel : un axe de catégories en
        // `boundaryGap: false` s'étend de la catégorie 0 à la N-1, soit
        // (N-1)/N du tour — il manque un secteur, et tout ce que l'axe trace
        // (cercles concentriques compris) s'y interrompt.
        type: "value",
        min: 0,
        max: 360,
        interval: demiSecteur * 2,
        clockwise: false,
        startAngle: 90 + demiSecteur,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: {
          show: true,
          lineStyle: { type: "dashed", color: TRAIT_SECTEUR, width: 1 },
        },
        z: 1,
      },
    ],
    radiusAxis: [
      {
        type: "value",
        min: 0,
        max: 100,
        splitNumber: 5,
        // Aucun cercle ici : ceux de ce polaire seraient tronqués du secteur
        // manquant. Ils sont tracés par le polaire 1, qui fait le tour complet.
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
      },
      {
        polarIndex: 1,
        type: "value",
        min: 0,
        max: 100,
        splitNumber: 5,
        z: 1,
        splitLine: {
          show: true,
          lineStyle: { type: "dotted", color: "rgba(0,0,0,0.15)" },
        },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
      },
    ],

    // ── Couche 2 : radar de couverture ─────────────────────────────────
    radar: {
      center: centre,
      radius: rayon,
      startAngle: 90,
      shape: "polygon",
      splitNumber: 5,
      indicator: lignes.map((l) => ({
        name: couperNom(l.competence, largeurNom),
        min: 0,
        max: 100,
      })),
      // La grille est déjà tracée par le système polaire : la redoubler
      // rendrait la figure illisible. Le radar n'apporte que les noms.
      axisLine: { show: false },
      splitLine: { show: false },
      splitArea: { show: false },
      axisName: {
        fontSize: policeNom,
        fontFamily: "sans-serif",
        lineHeight: interligneNom,
        color: jeton("--chart-axe", "#334155"),
      },
    },

    series: [
      {
        // Série vide rattachée au polaire jumeau : ECharts ne rend un système
        // de coordonnées que s'il porte au moins une série. Elle ne dessine
        // rien — seules ses `splitLine` d'axe sont visibles.
        name: "separateurs",
        type: "scatter",
        coordinateSystem: "polar",
        polarIndex: 1,
        silent: true,
        symbolSize: 0,
        tooltip: { show: false },
        data: [],
        z: 1,
      },
      {
        name: "Poids de la compétence",
        type: "bar",
        coordinateSystem: "polar",
        barCategoryGap: "12%",
        itemStyle: { borderColor: surface, borderWidth: 1 },
        // L'`emphasis` d'ECharts éclaircit la barre au survol : sur une série
        // dont l'opacité CODE le poids, ce repeint ferait lire un autre poids.
        // L'infobulle suffit à signaler ce qui est survolé.
        emphasis: { disabled: true },
        data: lignes.map((l) => ({
          // Étendue constante : le secteur couvre tout le rayon, seul son
          // contraste varie.
          value: 100,
          itemStyle: {
            color: couleur,
            opacity: opacitePoids(l.poids, poidsMax),
          },
        })),
        z: 2,
      },
      {
        name: "Couverture du candidat",
        type: "radar",
        symbol: "none",
        lineStyle: { width: epaisseurProfil, color: encre },
        // Pas d'`areaStyle` : un remplissage masquerait les secteurs, qui
        // portent la pondération.
        tooltip: { show: false },
        data: [{ value: lignes.map((l) => l.couverture) }],
        z: 5,
      },
      {
        // Points en scatter POLAIRE et non en symboles du radar : le scatter
        // accepte un style par point, et reste aligné sur les secteurs.
        name: "Couverture",
        type: "scatter",
        coordinateSystem: "polar",
        silent: true,
        data: lignes.map((l, index) => ({
          value: [l.couverture, index],
          symbolSize: taillePoint,
          itemStyle: {
            color: encre,
            borderColor: surface,
            borderWidth: 1.5,
          },
        })),
        z: 6,
      },
    ],
  };
}
